/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Chip } from "@mui/material";
import { useEffect, useState } from "react";
import { OptionColor } from "components/content/AppointmentCalendarContent";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/**
 * Base interface for autocomplete options requiring an ID.
 * @interface OptionBase
 * @property {string} [id] - Unique identifier for the option
 */
interface OptionBase {
  id?: string;
}

/**
 * Props for AutocompleteCheckboxes component.
 * @interface AutocompleteCheckboxesProps
 * @template T - Type extending OptionBase
 * @property {T[]} options - Array of selectable options
 * @property {string} label - Label text for the autocomplete field
 * @property {string} [placeholder] - Placeholder text (default: "Select...")
 * @property {(selectedOptions: T[]) => void} onChange - Callback when selection changes
 * @property {(option: T) => string} getOptionLabel - Function to extract label from option
 * @property {OptionColor[]} [optionColors] - Array of option colors for chips
 * @property {React.Dispatch<React.SetStateAction<OptionColor[]>>} [setOptionColors] - State setter for option colors
 * @property {T[]} [value] - Controlled value for selected options
 */
interface AutocompleteCheckboxesProps<T> {
  options: T[];
  label: string;
  placeholder?: string;
  onChange: (selectedOptions: T[]) => void;
  getOptionLabel: (option: T) => string;
  optionColors?: OptionColor[];
  setOptionColors?: React.Dispatch<React.SetStateAction<OptionColor[]>>;
  value?: T[];
}

/**
 * Generates a consistent HSL color based on string ID.
 * Uses ID's hex value to determine hue (0-360 degrees).
 * 
 * @param id - String identifier to generate color from
 * @returns HSL color string with 70% saturation and 45% lightness
 */
const generateColor = (id: string) => {
  const hue = parseInt(id, 16) % 360;
  return `hsl(${hue} 70% 45%)`;
};

/**
 * Multi-select autocomplete with checkbox selection and colored chips.
 * Generic component supporting any option type with an ID.
 * 
 * Features:
 * - Multiple selection with checkboxes
 * - Color-coded chips for selected items
 * - Automatic color generation from option IDs
 * - Persistent color assignment across selections
 * - Controlled and uncontrolled modes
 * - Customizable option labels
 * - Responsive width (100% mobile, 500px desktop)
 * - Close on select disabled for better UX
 * 
 * Color Management:
 * - Generates unique colors based on option IDs
 * - Maintains color consistency across re-selections
 * - Removes colors for deselected options
 * 
 * Use Cases:
 * - Tag selection with visual differentiation
 * - Multi-category filtering
 * - Template tag assignment
 * - Any multi-select scenario requiring color coding
 * 
 * @template T - Option type extending OptionBase
 * @param options - Available options to select from
 * @param label - Field label displayed above autocomplete
 * @param placeholder - Placeholder text in empty field
 * @param onChange - Callback with array of selected options
 * @param getOptionLabel - Extract display string from option object
 * @param optionColors - Current color assignments for options
 * @param setOptionColors - Update color assignments
 * @param value - Controlled selected options
 * @returns Multi-select autocomplete with colored chips
 */
function AutocompleteCheckboxes<T extends OptionBase>({
  options,
  label,
  placeholder = "Select...",
  onChange,
  getOptionLabel,
  optionColors,
  value,
  setOptionColors,
}: Readonly<AutocompleteCheckboxesProps<T>>) {
  const [selectedOptions, setSelectedOptions] = useState<T[]>([]);

  useEffect(() => {
    if (value) {
      setSelectedOptions(value);
    }
  }, [value]);

  useEffect(() => {
    setOptionColors((prevColors) => {
      const newColors = selectedOptions.map((option) => {
        const existingColorObj = prevColors.find((colorObj) => colorObj.id === option.id);
        if (existingColorObj) {
          return existingColorObj;
        } else {
          const color = generateColor(option.id);
          return {
            id: option.id,
            color: color,
          };
        }
      });

      const newColorIds = new Set(selectedOptions.map((option) => option.id));
      const filteredColors = prevColors.filter((colorObj) => newColorIds.has(colorObj.id));
      return [...filteredColors, ...newColors];
    });
  }, [selectedOptions]);

  const handleChange = (value: T[]) => {
    setSelectedOptions(value);
    onChange(value);
  };

  return (
    <Autocomplete
      multiple
      id="checkboxes-tags"
      options={options}
      disableCloseOnSelect
      getOptionLabel={getOptionLabel}
      value={selectedOptions}
      onChange={(event, value) => handleChange(value as T[])}
      renderOption={(props, option, { selected }) => {
        const { key, ...otherProps } = { ...props, key: undefined };
        return (
          <li key={option.id} {...otherProps}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {getOptionLabel(option)}
          </li>
        );
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const colorEntry = optionColors.find((colorObj) => colorObj.id === option.id);
          const backgroundColor = colorEntry ? colorEntry.color : generateColor(option.id);
          const tagProps = getTagProps({ index });
          const { key, ...otherTagProps } = tagProps;

          return (
            <Chip
              key={option.id}
              label={getOptionLabel(option)}
              style={{ backgroundColor, color: "white" }}
              {...otherTagProps}
            />
          );
        })
      }
      sx={{
        width: {
          xs: "100%",
          sm: "500px",
        },
        paddingBottom: {
          xs: "5px",
          sm: "0",
        },
      }}
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} />}
    />
  );
}

export default AutocompleteCheckboxes;
